import React, { useCallback, useMemo, useRef, useState } from "react";
import { createContainer } from "unstated-next";
import * as voicifyApi from '../api/controllers/scheduledAnalyticsEmail';
import { ScheduledAnalyticsEmail } from "../models/analytics/api/ScheduledAnalyticsEmail";
import IResult, { ResultType } from "../models/result/IResult";

type SortKey = "organization" | "application" | "created" | "modified" | "email" | "startDate" | "id";
type SortOrder = "asc" | "desc";

const compare = (a: string | number | Date | null | undefined, b: string | number | Date | null | undefined) => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (a instanceof Date || b instanceof Date) {
        const da = a instanceof Date ? a.getTime() : new Date(a).getTime();
        const db = b instanceof Date ? b.getTime() : new Date(b).getTime();
        return da - db;
    }
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b));
}

const sorter = (key: SortKey, order: SortOrder = "asc") => {
    return (a: ScheduledAnalyticsEmail, b: ScheduledAnalyticsEmail) => {
        let valA: any;
        let valB: any;

        switch (key) {
            case "organization":
                valA = a.organizationId;
                valB = b.organizationId;
                break;
            case "application":
                valA = a.applicationId;
                valB = b.applicationId;
                break;
            case "created":
                valA = a.eventDate ? new Date(a.eventDate) : null;
                valB = b.eventDate ? new Date(b.eventDate) : null;
                break;
            case "email":
                // if your model stores recipient email at e.email
                valA = (a as any)?.email ?? "";
                valB = (b as any)?.email ?? "";
                break;
            case "startDate":
                valA = a.emailStartDate ? new Date(a.emailStartDate) : null;
                valB = b.emailStartDate ? new Date(b.emailStartDate) : null;
                break;
            case "id":
            default:
                valA = a.id;
                valB = b.id;
                break;
        }

        const base = compare(valA, valB);
        return order === "asc" ? base : -base;
    };
}

/**
 * A small in-memory index for quick access/grouping.
 */
const indexEmails = (list: ScheduledAnalyticsEmail[]) => {
    const byId: Record<string, ScheduledAnalyticsEmail> = {};
    const byOrg: Record<string, ScheduledAnalyticsEmail[]> = {};
    const byApp: Record<string, ScheduledAnalyticsEmail[]> = {};
    const byOrgApp: Record<string, ScheduledAnalyticsEmail[]> = {};

    for (const e of list) {
        if (!e?.id) continue;

        byId[e.id] = e;

        if (e.organizationId) {
            if (!byOrg[e.organizationId]) byOrg[e.organizationId] = [];
            byOrg[e.organizationId].push(e);
        }

        if (e.applicationId) {
            if (!byApp[e.applicationId]) byApp[e.applicationId] = [];
            byApp[e.applicationId].push(e);
        }

        if (e.organizationId && e.applicationId) {
            const key = `${e.organizationId}|${e.applicationId}`;
            if (!byOrgApp[key]) byOrgApp[key] = [];
            byOrgApp[key].push(e);
        }
    }

    return { byId, byOrg, byApp, byOrgApp };
}

const mergeDistinct = (existing: ScheduledAnalyticsEmail[], incoming: ScheduledAnalyticsEmail[]) => {
    const seen = new Set<string>();
    const out: ScheduledAnalyticsEmail[] = [];
    for (const e of existing) {
        if (e?.id) {
            seen.add(e.id);
            out.push(e);
        }
    }
    for (const e of incoming) {
        if (e?.id && !seen.has(e.id)) {
            seen.add(e.id);
            out.push(e);
        }
    }
    return out;
}

const useScheduledAnalyticsEmailContainer = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [emails, setEmails] = useState<ScheduledAnalyticsEmail[]>([]);

    // Track fine-grained loading states for specific keys
    const loadingOrg = useRef<Set<string>>(new Set());
    const loadingOrgApp = useRef<Set<string>>(new Set());

    const { byId, byOrg, byApp, byOrgApp } = useMemo(() => indexEmails(emails), [emails]);

    const setError = useCallback((msg?: string) => {
        setErrors((prev) => (msg ? [...prev, msg] : prev));
    }, []);

    const clearErrors = useCallback(() => setErrors([]), []);

    // ---------- LOADERS ----------
    const loadForOrganization = useCallback(
        async (organizationId: string) => {
            if (!organizationId) return;

            loadingOrg.current.add(organizationId);
            setIsLoading(true);
            try {
                const res = await voicifyApi.getScheduledEmailsForOrganization(organizationId);
                if (res.resultType === "Ok") {
                    console.log(res)
                    // Replace existing entries for this org with the fresh ones
                    const existingWithoutOrg = emails.filter((e) => e.organizationId !== organizationId);
                    setEmails(mergeDistinct(existingWithoutOrg, res.data || []));
                } else {
                    setError(res.errors?.[0] || `Failed to load emails for org ${organizationId}`);
                }
            } catch (err: any) {
                setError(err?.message || `Failed to load emails for org ${organizationId}`);
            } finally {
                loadingOrg.current.delete(organizationId);
                setIsLoading(loadingOrg.current.size > 0 || loadingOrgApp.current.size > 0);
            }
        },
        [emails, setError]
    );

    const loadForApplication = useCallback(
        async (applicationId: string, organizationId: string) => {
            if (!applicationId || !organizationId) return;

            const key = `${organizationId}|${applicationId}`;
            loadingOrgApp.current.add(key);
            setIsLoading(true);
            try {
                const res = await voicifyApi.getResponsesForApplication(applicationId, organizationId);
                if (res.resultType === "Ok") {
                    // Remove existing rows for this org+app, then add fresh ones
                    const existingWithoutKey = emails.filter(
                        (e) => !(e.organizationId === organizationId && e.applicationId === applicationId)
                    );
                    setEmails(mergeDistinct(existingWithoutKey, res.data || []));
                } else {
                    setError(res.errors?.[0] || `Failed to load emails for app ${applicationId} in org ${organizationId}`);
                }
            } catch (err: any) {
                setError(err?.message || `Failed to load emails for app ${applicationId} in org ${organizationId}`);
            } finally {
                loadingOrgApp.current.delete(key);
                setIsLoading(loadingOrg.current.size > 0 || loadingOrgApp.current.size > 0);
            }
        },
        [emails, setError]
    );

    // ---------- CRUD (optimistic) ----------
    const create = useCallback(
        async (email: ScheduledAnalyticsEmail) => {
            // optimistic insert (temporary negative id if needed)
            const tempId = email.id || `temp-${Math.random().toString(36).slice(2)}`;
            const optimistic: ScheduledAnalyticsEmail = { ...email, id: tempId };

            setEmails((prev) => [optimistic, ...prev]);
            try {
                const res = await voicifyApi.createScheduledEmail(email);
                if (res.resultType === "Ok" && res.data) {
                    // replace temp with real
                    setEmails((prev) => prev.map((e) => (e.id === tempId ? res.data! : e)));
                    return res.data;
                } else {
                    // rollback
                    setEmails((prev) => prev.filter((e) => e.id !== tempId));
                    setError(res.errors?.[0] || "Failed to create scheduled email");
                    console.log(res.errors)
                }
            } catch (err: any) {
                setEmails((prev) => prev.filter((e) => e.id !== tempId));
                setError(err?.message || "Failed to create scheduled email");
                console.log(err)
            }
            return null;
        },
        [setError]
    );

    const update = useCallback(
        async (email: ScheduledAnalyticsEmail) => {
            if (!email?.id) return null;

            // optimistic update
            const prevSnapshot = byId[email.id];
            setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...prevSnapshot, ...email } : e)));

            try {
                const res = await voicifyApi.updateScheduledEmail(email);
                if (res.resultType === "Ok" && res.data) {
                    // ensure we use the server response
                    setEmails((prev) => prev.map((e) => (e.id === email.id ? res.data! : e)));
                    return res.data;
                } else {
                    // rollback
                    setEmails((prev) => prev.map((e) => (e.id === email.id ? prevSnapshot : e)));
                    setError(res.errors?.[0] || "Failed to update scheduled email");
                }
            } catch (err: any) {
                setEmails((prev) => prev.map((e) => (e.id === email.id ? prevSnapshot : e)));
                setError(err?.message || "Failed to update scheduled email");
            }
            return null;
        },
        [byId, setError]
    );

    const remove = useCallback(
        async (emailId: string) => {
            if (!emailId) return false;

            const prev = byId[emailId];
            if (!prev) return false;

            // optimistic remove
            setEmails((cur) => cur.filter((e) => e.id !== emailId));
            try {
                const res = await voicifyApi.deleteScheduledEmail(emailId);
                if (res.resultType === "Ok") {
                    return true;
                } else {
                    // rollback
                    setEmails((cur) => [prev, ...cur]);
                    setError(res.errors?.[0] || "Failed to delete scheduled email");
                }
            } catch (err: any) {
                setEmails((cur) => [prev, ...cur]);
                setError(err?.message || "Failed to delete scheduled email");
            }
            return false;
        },
        [byId, setError]
    );

    const preview = useCallback(
        async (orgId: string, orgName: string, excludedMetrics: string[], featureFlagId: string, appId?: string, appName?: string) => {
            if (!orgId) return null;
            const request = { organizationId: orgId, applicationId: appId, metricsToExclude: excludedMetrics, featureFlagId: featureFlagId, orgName: orgName, appName: appName }
            try {
                const res = await voicifyApi.previewScheduledEmail(request);
                if (res.resultType === "Ok") {
                    return res.data;
                } else {
                    setError(res.errors?.[0] || "Failed to delete scheduled email");
                }
            } catch (err: any) {
                setError(err?.message || "Failed to delete scheduled email");
            }
            return null;
        },
        [byId, setError]
    );

    // ---------- SELECTORS / HELPERS ----------
    const getById = useCallback((id: string) => byId[id], [byId]);

    const getForOrganization = useCallback(
        (organizationId: string, sortBy: SortKey = "created", order: SortOrder = "desc") => {
            const arr = byOrg[organizationId] || [];
            return [...arr].sort(sorter(sortBy, order));
        },
        [byOrg]
    );

    const getForApplication = useCallback(
        (applicationId: string, sortBy: SortKey = "created", order: SortOrder = "desc") => {
            const arr = byApp[applicationId] || [];
            return [...arr].sort(sorter(sortBy, order));
        },
        [byApp]
    );

    const getForOrgAndApp = useCallback(
        (organizationId: string, applicationId: string, sortBy: SortKey = "created", order: SortOrder = "desc") => {
            const key = `${organizationId}|${applicationId}`;
            const arr = byOrgApp[key] || [];
            return [...arr].sort(sorter(sortBy, order));
        },
        [byOrgApp]
    );

    const listSortedByOrganization = useCallback(
        (order: SortOrder = "asc") => [...emails].sort(sorter("organization", order)),
        [emails]
    );

    const listSortedByApplication = useCallback(
        (order: SortOrder = "asc") => [...emails].sort(sorter("application", order)),
        [emails]
    );

    const refreshOrg = useCallback((organizationId: string) => loadForOrganization(organizationId), [loadForOrganization]);
    const refreshApp = useCallback(
        (applicationId: string, organizationId: string) => loadForApplication(applicationId, organizationId),
        [loadForApplication]
    );

    const clearAll = useCallback(() => {
        setEmails([]);
        clearErrors();
    }, [clearErrors]);

    return {
        // state
        isLoading,
        errors,
        emails,

        // indexes
        byId,
        byOrg,
        byApp,
        byOrgApp,

        // loaders
        loadForOrganization,
        loadForApplication,
        refreshOrg,
        refreshApp,

        // crud
        create,
        update,
        remove,
        preview,

        // selectors
        getById,
        getForOrganization,
        getForApplication,
        getForOrgAndApp,
        listSortedByOrganization,
        listSortedByApplication,

        // utils
        clearErrors,
        clearAll,
    };
}

const ScheduledAnalyticsEmailContainer = createContainer(useScheduledAnalyticsEmailContainer);
export default ScheduledAnalyticsEmailContainer;
